import type { NavDropdownCategory } from "@/components/lembra/NavDropdown";
import {
  BookOpen,
  Briefcase,
  Coffee,
  LayoutGrid,
  PartyPopper,
  Repeat,
  Rocket,
  Salad,
  Sandwich,
  Sparkles,
  Store,
  UserPlus,
  Users,
  Utensils,
  Zap,
} from "lucide-react";

/** Sister-product origin — every catering leaf is a full URL escape from the celebrations SPA. */
export const CATERING_SITE_ORIGIN = "https://catering.lembra.com.au";

const cateringPath = (path: string) =>
  `${CATERING_SITE_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;

/** Seeds the catering landing SmartIntentBar via `/?q=`, matching catering navStructure.ts. */
const cateringSeedHref = (brief: string) =>
  `${CATERING_SITE_ORIGIN}/?q=${encodeURIComponent(brief)}`;

/**
 * Celebrations landing → Catering mega-dropdown.
 * Mirrors actionable paths from lembra-catering `navStructure.ts` (templates,
 * order-shape seeds, product entry points) — not the retired "by team size /
 * by style" stubs that all dumped users on the homepage.
 */
export const LANDING_CATERING_CATEGORIES: NavDropdownCategory[] = [
  {
    id: "examples",
    label: "Event examples",
    icon: Sparkles,
    items: [
      {
        label: "All-hands",
        icon: Users,
        href: cateringPath("/templates/all-hands"),
        external: true,
      },
      {
        label: "Onboarding welcome",
        icon: UserPlus,
        href: cateringPath("/templates/onboarding"),
        external: true,
      },
      {
        label: "Sprint demo / Friday wrap",
        icon: Zap,
        href: cateringPath("/templates/sprint-demo"),
        external: true,
      },
      {
        label: "EOFY / Christmas",
        icon: Sparkles,
        href: cateringPath("/templates/eofy-christmas"),
        external: true,
      },
      {
        label: "Launch party",
        icon: Rocket,
        href: cateringPath("/templates/launch-party"),
        external: true,
      },
      {
        label: "Customer dinner",
        icon: Utensils,
        href: cateringPath("/templates/customer-dinner"),
        external: true,
      },
    ],
  },
  {
    id: "orders",
    label: "Common orders",
    icon: Coffee,
    items: [
      {
        label: "Office breakfast",
        icon: Coffee,
        href: cateringSeedHref(
          "Office breakfast for the team with pastries, fruit, and coffee.",
        ),
        external: true,
      },
      {
        label: "Group order",
        icon: Sandwich,
        href: cateringSeedHref(
          "Boxed lunches for the team, group order for the office.",
        ),
        external: true,
      },
      {
        label: "Office party",
        icon: PartyPopper,
        href: cateringSeedHref("Office party with drinks and shared food."),
        external: true,
      },
      {
        label: "Recurring meals",
        icon: Repeat,
        href: cateringSeedHref(
          "Recurring office lunches with the same shape every week or month.",
        ),
        external: true,
      },
      {
        label: "Dietary-led catering",
        icon: Salad,
        href: cateringSeedHref(
          "Catering with a dietary focus: vegan, halal, or gluten-free as the default.",
        ),
        external: true,
      },
    ],
  },
  {
    id: "explore",
    label: "Explore catering",
    icon: BookOpen,
    items: [
      {
        label: "How Lembra works",
        icon: BookOpen,
        href: cateringPath("/how-it-works"),
        external: true,
      },
      {
        label: "Vendor directory",
        icon: Store,
        href: cateringPath("/vendors"),
        external: true,
      },
      {
        label: "All templates",
        icon: LayoutGrid,
        href: cateringPath("/templates"),
        external: true,
      },
      {
        label: "Describe your event",
        icon: Briefcase,
        href: cateringPath("/?focus=brief"),
        external: true,
      },
    ],
  },
];
